/**
 * StoryCard — grid card for a story on the My Writing dashboard.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MoreHorizontal, Star, BookOpen, Copy, Archive, ArchiveRestore,
  Trash2, Download, Send, Pencil,
} from 'lucide-react';
import { TiltCard, cx } from '@/components/site/primitives';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/sonner';
import StoryCover from './StoryCover';
import { useMyWriting } from './context/MyWritingContext';
import { getStoryWordCount, formatRelativeTime, buildExport, downloadTextFile } from './utils/storyHelpers';
import { STATUS } from './constants';

const STATUS_BADGE = {
  draft: 'bg-blue/15 text-[#4d616c] dark:text-[#aabecb]',
  completed: 'bg-sage/40 text-[#4f6249] dark:bg-sage/20 dark:text-[#b3c7aa]',
  published: 'bg-coral/12 text-coral',
  archived: 'bg-line/60 text-subtle',
};

export default function StoryCard({ story }) {
  const navigate = useNavigate();
  const { toggleFavorite, duplicateStory, archiveStory, restoreStory, deleteStory, updateStory } = useMyWriting();
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState(story.title);

  const wordCount = getStoryWordCount(story);
  const chapterCount = story.chapters?.length ?? 0;

  const commitRename = () => {
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== story.title) updateStory(story.id, { title: trimmed });
    setIsRenaming(false);
  };

  const handleExport = (format) => {
    const { content, ext, mime } = buildExport(story, format);
    downloadTextFile(`${story.title.replace(/[^\w\-]+/g, '_') || 'story'}.${ext}`, content, mime);
    toast(`Exported “${story.title}” as ${ext.toUpperCase()}`);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete “${story.title}”? This can't be undone.`)) {
      deleteStory(story.id);
      toast('Story deleted');
    }
  };

  return (
    <TiltCard max={4} className="group flex h-full flex-col rounded-2xl border border-line bg-card p-4 shadow-soft transition-shadow duration-300 hover:shadow-card" data-testid="my-writing-story-card">
      <div className="flex gap-3">
        <Link to={`/app/studio/${story.id}`} className="shrink-0">
          <StoryCover story={story} className="h-28 w-20 shadow-sm" />
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            {isRenaming ? (
              <input
                autoFocus
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') { setDraftTitle(story.title); setIsRenaming(false); }
                }}
                className="w-full rounded-md border border-coral/50 bg-cream px-1.5 py-0.5 font-display text-base font-semibold text-ink outline-none"
              />
            ) : (
              <Link to={`/app/studio/${story.id}`} className="min-w-0">
                <h3 className="truncate font-display text-base font-semibold leading-snug text-ink">{story.title}</h3>
              </Link>
            )}

            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                aria-label={story.isFavorite ? 'Unstar story' : 'Star story'}
                onClick={() => toggleFavorite(story.id)}
                className="grid h-7 w-7 place-items-center rounded-full text-subtle transition-colors hover:text-yellow"
              >
                <Star className={cx('h-4 w-4', story.isFavorite && 'fill-yellow text-yellow')} />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" aria-label="Story options" className="grid h-7 w-7 place-items-center rounded-full text-subtle transition-colors hover:bg-cream hover:text-ink">
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44 rounded-xl border-line bg-card text-ink">
                  <DropdownMenuItem onClick={() => navigate(`/app/studio/${story.id}`)}>
                    <BookOpen className="mr-2 h-3.5 w-3.5" /> Continue
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsRenaming(true)}>
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { duplicateStory(story.id); toast('Story duplicated'); }}>
                    <Copy className="mr-2 h-3.5 w-3.5" /> Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleExport('txt')}>
                    <Download className="mr-2 h-3.5 w-3.5" /> Export as TXT
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('markdown')}>
                    <Download className="mr-2 h-3.5 w-3.5" /> Export as Markdown
                  </DropdownMenuItem>
                  {story.status !== 'published' && (
                    <DropdownMenuItem onClick={() => navigate(`/app/studio/${story.id}?publish=1`)}>
                      <Send className="mr-2 h-3.5 w-3.5" /> Publish
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {story.status === 'archived' ? (
                    <DropdownMenuItem onClick={() => restoreStory(story.id)}>
                      <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> Restore
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => { archiveStory(story.id); toast('Moved to archive'); }}>
                      <Archive className="mr-2 h-3.5 w-3.5" /> Move to archive
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleDelete} className="text-coral focus:text-coral">
                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {story.description && (
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-subtle">{story.description}</p>
          )}

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {story.genre && <span className="rounded-full bg-cream px-2 py-0.5 text-[10px] font-semibold text-subtle">{story.genre}</span>}
            <span className={cx('rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_BADGE[story.status])}>
              {STATUS[story.status]?.label ?? story.status}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-line pt-3 text-[11px] text-subtle">
        <span>{chapterCount} {chapterCount === 1 ? 'chapter' : 'chapters'} · {wordCount.toLocaleString()} words</span>
        <span>Edited {formatRelativeTime(story.updatedAt)}</span>
      </div>
    </TiltCard>
  );
}
